import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useStore } from '../context/StoreContext';
import { searchEngine } from '../utils/search';
import SafeIcon from '../common/SafeIcon';
import { APP_VIEW_DASHBOARD, APP_VIEW_SETTINGS, APP_VIEW_BACKUP, APP_VIEW_BOARD } from '../constants';
import { copyNote, getRecentNoteIds, recordRecentNote } from '../utils/productivity';

const DEFAULT_ACTIONS = [
  { id: 'action-new-text', title: 'Create New Text Note', icon: 'FileText' },
  { id: 'action-quick-capture', title: 'Open Quick Capture', icon: 'Zap' },
  { id: 'action-clipboard-history', title: 'Open Clipboard History', icon: 'Clipboard' },
  { id: 'action-sync', title: 'Sync Now', icon: 'RefreshCw' },
  { id: 'nav-dashboard', title: 'Go to Dashboard', icon: 'Layout' },
  { id: 'nav-favorites', title: 'Show Favorites', icon: 'Heart' },
  { id: 'nav-recent', title: 'Show Recently Viewed', icon: 'Clock' }
];

const Highlight = ({ text, query }) => {
  const value = String(text || '');
  const q = String(query || '').trim();
  if (!q) return value;
  const idx = value.toLowerCase().indexOf(q.toLowerCase());
  if (idx < 0) return value;
  return <>{value.slice(0, idx)}<mark className="bg-yellow-100 text-inherit rounded px-0.5">{value.slice(idx, idx + q.length)}</mark>{value.slice(idx + q.length)}</>;
};

const CommandPalette = () => {
  const {
    isPaletteOpen, setIsPaletteOpen, notes, workspaces, templates,
    setEditingNote, createNote, setFocusMode, setViewMode,
    setActiveWorkspaceId, selectedNoteIds, bulkArchive,
    bulkDeletePermanently, setShowArchived, runSync, editingNote
  } = useStore();

  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scope, setScope] = useState('all');
  const [dateRange, setDateRange] = useState('any');
  const [workspaceFilter, setWorkspaceFilter] = useState('all');
  const inputRef = useRef(null);

  useEffect(() => {
    const handleGlobalShortcut = e => {
      const key = String(e.key || '').toLowerCase();
      const commandPressed = e.ctrlKey || e.metaKey;
      const paletteShortcut = commandPressed && !e.altKey && !e.shiftKey && (key === 'k' || key === '/');
      if (!paletteShortcut) return;

      // Ctrl/Cmd+K is also a browser address/search shortcut. Capture it at the
      // window level so StickyHQ receives it while the app has focus.
      e.preventDefault();
      e.stopPropagation();
      setIsPaletteOpen(prev => !prev);
    };

    window.addEventListener('keydown', handleGlobalShortcut, true);
    return () => window.removeEventListener('keydown', handleGlobalShortcut, true);
  }, [setIsPaletteOpen]);

  useEffect(() => {
    if (!isPaletteOpen) return;
    const timer = setTimeout(() => inputRef.current?.focus(), 50);
    setQuery('');
    setSelectedIndex(0);
    return () => clearTimeout(timer);
  }, [isPaletteOpen]);

  const recentIds = getRecentNoteIds();
  const filteredNotes = useMemo(() => {
    const now = Date.now();
    const maxAge = dateRange === 'today' ? 86400000 : dateRange === '7d' ? 7 * 86400000 : dateRange === '30d' ? 30 * 86400000 : Infinity;
    return notes.filter(note => {
      if (note.deletedAt) return false;
      if (workspaceFilter !== 'all' && note.workspaceId !== workspaceFilter) return false;
      if (scope === 'favorites' && !note.favorite) return false;
      if (scope === 'attachments' && !(note.attachments || []).length) return false;
      if (scope === 'pinned' && !note.pinned) return false;
      if (scope === 'inbox' && !note.inbox) return false;
      if (Number.isFinite(maxAge) && now - Number(note.updatedAt || 0) > maxAge) return false;
      return true;
    });
  }, [notes, scope, dateRange, workspaceFilter]);

  const results = useMemo(() => {
    const base = searchEngine(query, filteredNotes, workspaces, templates);
    const actions = query.trim() ? [...base.actions] : [...DEFAULT_ACTIONS];
    if (editingNote) actions.unshift({ id: 'action-copy-open', title: 'Copy Open Note', icon: 'Copy' });
    if (selectedNoteIds.length > 0) {
      actions.unshift(
        { id: 'bulk-archive', title: `Archive ${selectedNoteIds.length} Selected Notes`, icon: 'Archive' },
        { id: 'bulk-delete', title: `Delete ${selectedNoteIds.length} Selected Notes`, icon: 'Trash2' }
      );
    }
    const defaultNotes = query.trim()
      ? base.notes
      : [...filteredNotes].sort((a, b) => {
          const ai = recentIds.indexOf(a.id);
          const bi = recentIds.indexOf(b.id);
          if (ai >= 0 || bi >= 0) return (ai < 0 ? 999 : ai) - (bi < 0 ? 999 : bi);
          return Number(b.updatedAt || 0) - Number(a.updatedAt || 0);
        }).slice(0, 14);
    return { actions, workspaces: base.workspaces, notes: defaultNotes };
  }, [query, filteredNotes, workspaces, templates, selectedNoteIds, editingNote, recentIds]);

  const totalItems = results.actions.length + results.workspaces.length + results.notes.length;

  useEffect(() => setSelectedIndex(0), [query, scope, dateRange, workspaceFilter]);

  const executeAction = async index => {
    const { actions, workspaces: wsResults, notes: noteResults } = results;
    if (index < actions.length) {
      const item = actions[index];
      if (item.id === 'action-new-text') createNote('text');
      else if (item.id === 'action-new-check') createNote('checklist');
      else if (item.id === 'action-new-table') createNote('table');
      else if (item.id === 'action-quick-capture') window.dispatchEvent(new CustomEvent('stickyhq:open-quick-capture'));
      else if (item.id === 'action-clipboard-history') window.dispatchEvent(new CustomEvent('stickyhq:open-clipboard-history'));
      else if (item.id === 'action-sync') await runSync('command_palette');
      else if (item.id === 'action-focus') setFocusMode(p => !p);
      else if (item.id === 'action-copy-open' && editingNote) await copyNote(editingNote, editingNote.bodyHtml ? 'rich' : 'plain');
      else if (item.id === 'nav-dashboard') setViewMode(APP_VIEW_DASHBOARD);
      else if (item.id === 'nav-settings') setViewMode(APP_VIEW_SETTINGS);
      else if (item.id === 'nav-backup') setViewMode(APP_VIEW_BACKUP);
      else if (item.id === 'nav-archive') { setViewMode(APP_VIEW_BOARD); setShowArchived(true); }
      else if (item.id === 'nav-favorites') { setViewMode(APP_VIEW_DASHBOARD); window.dispatchEvent(new CustomEvent('stickyhq:dashboard-section', { detail: 'favorites' })); }
      else if (item.id === 'nav-recent') { setViewMode(APP_VIEW_DASHBOARD); window.dispatchEvent(new CustomEvent('stickyhq:dashboard-section', { detail: 'recent' })); }
      else if (item.id === 'bulk-archive') await bulkArchive(selectedNoteIds);
      else if (item.id === 'bulk-delete') await bulkDeletePermanently(selectedNoteIds);
    } else if (index < actions.length + wsResults.length) {
      const ws = wsResults[index - actions.length];
      setActiveWorkspaceId(ws.id);
      setViewMode(APP_VIEW_BOARD);
    } else {
      const note = noteResults[index - actions.length - wsResults.length];
      if (note) {
        recordRecentNote(note.id);
        setEditingNote(note);
      }
    }
    setIsPaletteOpen(false);
  };

  const handleKeyDown = e => {
    if (e.key === 'Escape') setIsPaletteOpen(false);
    else if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedIndex(prev => (prev + 1) % Math.max(totalItems, 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSelectedIndex(prev => (prev - 1 + totalItems) % Math.max(totalItems, 1)); }
    else if (e.key === 'Enter' && totalItems > 0) { e.preventDefault(); void executeAction(selectedIndex); }
  };

  if (!isPaletteOpen) return null;

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[9999] flex items-end sm:items-start justify-center sm:pt-[8vh] bg-black/40 backdrop-blur-sm p-0 sm:p-4 safe-top safe-bottom" onClick={() => setIsPaletteOpen(false)}>
        <motion.div initial={{ scale: 0.96, opacity: 0, y: -14 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.96, opacity: 0, y: -14 }} className="w-full max-w-3xl bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden border border-gray-100 flex flex-col max-h-[90dvh] sm:max-h-[80dvh]" onClick={e => e.stopPropagation()}>
          <div className="flex items-center px-4 py-3 border-b border-gray-100 shrink-0">
            <SafeIcon name="Search" className="text-gray-400 text-xl mr-3" />
            <input ref={inputRef} value={query} onChange={e => setQuery(e.target.value)} onKeyDown={handleKeyDown} placeholder="Search notes, attachments, tags or run a command…" className="w-full bg-transparent text-lg text-gray-800 placeholder-gray-400 focus:outline-none py-1" />
            <button onClick={() => setIsPaletteOpen(false)} className="min-w-11 min-h-11 p-2 text-gray-400 hover:text-gray-600 ml-2 rounded-xl"><SafeIcon name="X" className="text-lg" /></button>
          </div>

          <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/70 flex flex-wrap gap-2 items-center">
            <select value={workspaceFilter} onChange={e => setWorkspaceFilter(e.target.value)} className="min-h-11 px-3 py-2 bg-white border border-gray-200 rounded-xl text-sm font-bold text-gray-600 outline-none">
              <option value="all">All workspaces</option>
              {workspaces.filter(w => !w.archivedAt).map(ws => <option key={ws.id} value={ws.id}>{ws.name}</option>)}
            </select>
            {['all', 'favorites', 'attachments', 'pinned', 'inbox'].map(value => <button key={value} onClick={() => setScope(value)} className={`min-h-11 px-3 py-2 rounded-xl text-sm font-bold capitalize ${scope === value ? 'bg-blue-600 text-white' : 'bg-white border border-gray-200 text-gray-600'}`}>{value}</button>)}
            <select value={dateRange} onChange={e => setDateRange(e.target.value)} className="px-2.5 py-2 bg-white border border-gray-200 rounded-lg text-xs font-bold text-gray-600 outline-none">
              <option value="any">Any date</option><option value="today">Today</option><option value="7d">Last 7 days</option><option value="30d">Last 30 days</option>
            </select>
          </div>

          <div className="flex-1 overflow-y-auto p-2 no-scrollbar">
            {results.actions.length > 0 && (
              <div className="mb-4">
                <div className="px-3 py-1 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Commands</div>
                {results.actions.map((action, idx) => <button key={`${action.id}-${idx}`} onClick={() => void executeAction(idx)} className={`w-full min-h-12 flex items-center px-4 py-3 rounded-xl text-left transition-colors ${selectedIndex === idx ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-50'}`}><SafeIcon name={action.icon} className="mr-3 text-lg" /><span className="font-medium">{action.title}</span></button>)}
              </div>
            )}

            {results.workspaces.length > 0 && (
              <div className="mb-4">
                <div className="px-3 py-1 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Workspaces</div>
                {results.workspaces.map((ws, idx) => { const actualIdx = idx + results.actions.length; return <button key={ws.id} onClick={() => void executeAction(actualIdx)} className={`w-full min-h-12 flex items-center px-4 py-3 rounded-xl text-left ${selectedIndex === actualIdx ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-50'}`}><div className="w-2.5 h-2.5 rounded-full mr-3" style={{ backgroundColor: ws.color }} /><span className="font-medium"><Highlight text={ws.name} query={query} /></span></button>; })}
              </div>
            )}

            <div>
              <div className="px-3 py-1 flex items-center justify-between"><span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Notes</span><span className="text-[10px] text-gray-400">{results.notes.length} shown</span></div>
              {results.notes.length === 0 ? <div className="p-10 text-center text-sm text-gray-400">No notes match these filters.</div> : results.notes.map((note, idx) => {
                const actualIdx = idx + results.actions.length + results.workspaces.length;
                return <button key={note.id} onClick={() => void executeAction(actualIdx)} className={`w-full min-h-12 flex flex-col px-4 py-3 rounded-xl text-left ${selectedIndex === actualIdx ? 'bg-blue-50' : 'hover:bg-gray-50'}`}>
                  <div className="flex items-center gap-2 w-full"><span className={`font-bold flex-1 truncate ${selectedIndex === actualIdx ? 'text-blue-800' : 'text-gray-800'}`}><Highlight text={note.title || 'Untitled Note'} query={query} /></span>{note.favorite && <SafeIcon name="Heart" className="text-rose-500 fill-current" />}{(note.attachments || []).length > 0 && <span className="text-[10px] text-gray-400 flex items-center gap-1"><SafeIcon name="Paperclip" />{note.attachments.length}</span>}</div>
                  <div className="text-xs text-gray-500 truncate mt-0.5 w-full"><Highlight text={note.bodyText || note.attachments?.map(a => a.name).filter(Boolean).join(' · ') || 'Empty note'} query={query} /></div>
                </button>;
              })}
            </div>
          </div>

          <div className="p-3 bg-gray-50 border-t border-gray-100 hidden md:flex items-center justify-between text-[10px] font-bold uppercase tracking-widest text-gray-400"><span>Enter select · ↑↓ navigate</span><span>Ctrl/Cmd+K or Ctrl/Cmd+/ · Esc close</span></div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CommandPalette;